import React, { useState, useEffect } from 'react';
import {
    Table, TableHead, TableBody, TableRow, TableCell,
    Paper, TableContainer, Button, TextField, Box,
    CircularProgress, IconButton, Dialog, DialogActions,
    DialogContent, DialogTitle, Typography, Chip, Tabs, Tab,
    Select, MenuItem, FormControl, InputLabel, Switch,
    FormControlLabel, Divider, Card, CardContent, Grid,
    Accordion, AccordionSummary, AccordionDetails, Alert,
    LinearProgress, Tooltip, FormHelperText, Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import client from '../../../../../API';
import { toast } from 'sonner';

// Campaign type options
const CAMPAIGN_TYPES = {
    SYSTEM: ['timeline_notifications', 'deliverable_reminders', 'unread_message_reminders', 'conversation_digests', 'campaign_manager_updates', 'push_notification_summaries'],
    MARKETING: ['inactive_user_reminders', 'campaign_opportunities', 'feature_announcements', 'weekly_creator_newsletter', 'product_updates'],
    MAINTENANCE: ['reauth_notifications', 'profile_completion_reminders', 'creatorconnect_invitations', 'security_alerts'],
    EXTERNAL: ['external']
};

// Audience type options
const AUDIENCE_TYPES = [
    { value: 'users', label: 'Users (UserModel)' },
    { value: 'creators', label: 'Creators (CreatorModel)' },
    { value: 'creator_crm', label: 'Creator CRM (External)' },
    { value: 'crm_contacts', label: 'CRM Contacts (External)' },
    { value: 'custom_list', label: 'Custom Email List' },
    { value: 'mixed', label: 'Mixed Audiences' }
];

// Filter configurations by audience type
const FILTER_CONFIGS = {
    users: [
        { key: 'last_login_before', label: 'Last Login Before', type: 'text', placeholder: '14_days_ago' },
        { key: 'last_login_after', label: 'Last Login After', type: 'text', placeholder: '7_days_ago' },
        { key: 'created_before', label: 'Created Before', type: 'text', placeholder: '30_days_ago' },
        { key: 'has_active_campaigns', label: 'Has Active Campaigns', type: 'boolean' },
        { key: 'has_active_partnerships', label: 'Has Active Partnerships', type: 'boolean' },
        { key: 'has_payouts', label: 'Has Payouts', type: 'boolean' },
        { key: 'last_payout_before', label: 'Last Payout Before', type: 'text', placeholder: '21_days_ago' },
        { key: 'no_profile_photo', label: 'No Profile Photo', type: 'boolean' },
        { key: 'exclude_user_ids', label: 'Exclude User IDs', type: 'array', placeholder: '1,2,3' }
    ],
    creators: [
        { key: 'manager', label: 'Manager', type: 'text', placeholder: 'TCC' },
        { key: 'has_creatorconnect', label: 'Has CreatorConnect', type: 'boolean' },
        { key: 'has_partnerships', label: 'Has Partnerships', type: 'boolean' },
        { key: 'has_campaigns', label: 'Has Campaigns', type: 'boolean' },
        { key: 'has_payouts', label: 'Has Payouts', type: 'boolean' },
        { key: 'needs_reauth', label: 'Needs Reauth', type: 'boolean' },
        { key: 'last_creatorconnect_email_before', label: 'Last CC Email Before', type: 'text', placeholder: '13_days_ago' }
    ],
    creator_crm: [
        { key: 'status', label: 'Status', type: 'text', placeholder: 'cold' },
        { key: 'following_min', label: 'Min Followers', type: 'number', placeholder: '100000' },
        { key: 'following_max', label: 'Max Followers', type: 'number', placeholder: '1000000' },
        { key: 'free_email_only', label: 'Free Email Only', type: 'boolean' },
        { key: 'last_updated_before', label: 'Last Updated Before', type: 'text', placeholder: '30_days_ago' },
        { key: 'exclude_if_creator_exists', label: 'Exclude if Creator Exists', type: 'boolean' },
        { key: 'batch_size', label: 'Batch Size', type: 'number', placeholder: '100' }
    ],
    crm_contacts: [
        { key: 'contact_source', label: 'Contact Source', type: 'text', placeholder: 'brand_deals' },
        { key: 'status', label: 'Status', type: 'text', placeholder: 'EMAIL1' },
        { key: 'exclude_if_user_exists', label: 'Exclude if User Exists', type: 'boolean' },
        { key: 'batch_size', label: 'Batch Size', type: 'number', placeholder: '500' }
    ],
    custom_list: [
        { key: 'emails', label: 'Email Addresses', type: 'textarea', placeholder: 'one@example.com\ntwo@example.com' }
    ]
};

const InternalCampaigns = () => {
    const [campaignsData, setCampaignsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [campaignTypes, setCampaignTypes] = useState(null);
    
    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
    
    // Tab state
    const [activeTab, setActiveTab] = useState(0);
    
    // Form state for new/edit campaign
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        campaign_type: '',
        target_audience: {
            type: 'users',
            filters: {}
        },
        schedule_config: {
            frequency: 'weekly',
            day_of_week: 'monday',
            hour: 10,
            batch_size: 100
        },
        ab_test_enabled: false,
        ab_test_config: {
            variants: ['A', 'B'],
            split: [0.5, 0.5],
            winning_metric: 'open_rate'
        },
        messages: [{
            sequence_number: 1,
            delay_days: 0,
            subject: '',
            content_variants: {
                default: ''
            },
            template_vars: ['first_name', 'email']
        }],
        status: 'draft'
    });
    
    // Test email state
    const [testEmails, setTestEmails] = useState('');
    const [testVariant, setTestVariant] = useState('default');

    // ============================================================================
    // DATA FETCHING
    // ============================================================================

    const fetchCampaigns = async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await client.emailCampaigns.list(filters);
            setCampaignsData(res.campaigns || []);
            toast.success('Campaigns loaded', {
                description: `${res.total || 0} campaigns found`
            });
        } catch (err) {
            console.error("Error fetching campaigns:", err);
            setError(err);
            toast.error('Failed to load campaigns');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCampaignDetails = async (campaignId) => {
        try {
            const res = await client.emailCampaigns.get(campaignId);
            setSelectedCampaign(res.campaign);
            return res;
        } catch (err) {
            console.error("Error fetching campaign details:", err);
            toast.error('Failed to load campaign details');
        }
    };

    const fetchAnalytics = async (campaignId) => {
        try {
            const res = await client.emailCampaigns.getAnalytics(campaignId);
            setAnalytics(res);
            return res;
        } catch (err) {
            console.error("Error fetching analytics:", err);
            toast.error('Failed to load analytics');
        }
    };

    const fetchRecipients = async (campaignId, filters = {}) => {
        try {
            const res = await client.emailCampaigns.getRecipients(campaignId, filters);
            setRecipients(res.recipients || []);
            return res;
        } catch (err) {
            console.error("Error fetching recipients:", err);
            toast.error('Failed to load recipients');
        }
    };

    const fetchTemplates = async () => {
        try {
            const res = await client.emailCampaigns.getTemplates();
            setTemplates(res.templates || []);
        } catch (err) {
            console.error("Error fetching templates:", err);
            toast.error('Failed to load templates');
        }
    };

    const fetchCampaignTypes = async () => {
        try {
            const res = await client.emailCampaigns.getCampaignTypes();
            setCampaignTypes(res);
        } catch (err) {
            console.error("Error fetching campaign types:", err);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        fetchTemplates();
        fetchCampaignTypes();
    }, []);

    // ============================================================================
    // CAMPAIGN CRUD OPERATIONS
    // ============================================================================

    const createCampaign = async () => {
        setIsLoading(true);
        try {
            await client.emailCampaigns.create(formData);
            toast.success('Campaign created successfully');
            await fetchCampaigns();
            setCreateDialogOpen(false);
            resetFormData();
        } catch (err) {
            console.error("Error creating campaign:", err);
            toast.error('Failed to create campaign', {
                description: err.response?.data?.error || err.message
            });
        } finally {
            setIsLoading(false);
        }
    };

    const updateCampaign = async (campaignId, updates) => {
        setIsLoading(true);
        try {
            await client.emailCampaigns.update(campaignId, updates);
            toast.success('Campaign updated successfully');
            await fetchCampaigns();
            setEditDialogOpen(false);
        } catch (err) {
            console.error("Error updating campaign:", err);
            toast.error('Failed to update campaign');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteCampaign = async (campaignId) => {
        if (!window.confirm('Are you sure you want to delete this campaign?')) {
            return;
        }
        
        setIsLoading(true);
        try {
            await client.emailCampaigns.delete(campaignId);
            toast.success('Campaign deleted successfully');
            await fetchCampaigns();
        } catch (err) {
            console.error("Error deleting campaign:", err);
            toast.error('Failed to delete campaign');
        } finally {
            setIsLoading(false);
        }
    };

    const sendTestEmail = async (campaignId) => {
        try {
            const emails = testEmails.split(',').map(e => e.trim()).filter(e => e);
            if (emails.length === 0) {
                toast.error('Please enter at least one email address');
                return;
            }

            await client.emailCampaigns.sendTest(campaignId, {
                test_emails: emails,
                variant: testVariant
            });
            
            toast.success('Test emails sent', {
                description: `Sent to ${emails.length} recipient(s)`
            });
            setTestDialogOpen(false);
            setTestEmails('');
        } catch (err) {
            console.error("Error sending test email:", err);
            toast.error('Failed to send test emails');
        }
    };

    const createFromTemplate = async (templateId, overrides = {}) => {
        setIsLoading(true);
        try {
            await client.emailCampaigns.createFromTemplate(templateId, overrides);
            toast.success('Campaign created from template');
            await fetchCampaigns();
            setTemplatesDialogOpen(false);
        } catch (err) {
            console.error("Error creating from template:", err);
            toast.error('Failed to create campaign from template');
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================================================
    // FORM HANDLERS
    // ============================================================================

    const resetFormData = () => {
        setFormData({
            name: '',
            description: '',
            campaign_type: '',
            target_audience: {
                type: 'users',
                filters: {}
            },
            schedule_config: {
                frequency: 'weekly',
                day_of_week: 'monday',
                hour: 10,
                batch_size: 100
            },
            ab_test_enabled: false,
            ab_test_config: {
                variants: ['A', 'B'],
                split: [0.5, 0.5],
                winning_metric: 'open_rate'
            },
            messages: [{
                sequence_number: 1,
                delay_days: 0,
                subject: '',
                content_variants: {
                    default: ''
                },
                template_vars: ['first_name', 'email']
            }],
            status: 'draft'
        });
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNestedChange = (path, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const keys = path.split('.');
            let current = newData;
            
            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }
            
            current[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    const handleMessageChange = (index, field, value) => {
        setFormData(prev => {
            const newMessages = [...prev.messages];
            newMessages[index] = {
                ...newMessages[index],
                [field]: value
            };
            return {
                ...prev,
                messages: newMessages
            };
        });
    };

    const handleContentVariantChange = (messageIndex, variant, content) => {
        setFormData(prev => {
            const newMessages = [...prev.messages];
            newMessages[messageIndex] = {
                ...newMessages[messageIndex],
                content_variants: {
                    ...newMessages[messageIndex].content_variants,
                    [variant]: content
                }
            };
            return {
                ...prev,
                messages: newMessages
            };
        });
    };

    const addMessage = () => {
        setFormData(prev => ({
            ...prev,
            messages: [
                ...prev.messages,
                {
                    sequence_number: prev.messages.length + 1,
                    delay_days: 0,
                    subject: '',
                    content_variants: { default: '' },
                    template_vars: ['first_name', 'email']
                }
            ]
        }));
    };

    const removeMessage = (index) => {
        setFormData(prev => ({
            ...prev,
            messages: prev.messages
                .filter((_, i) => i !== index)
                .map((msg, idx) => ({ ...msg, sequence_number: idx + 1 }))
        }));
    };

    const handleFilterChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            target_audience: {
                ...prev.target_audience,
                filters: {
                    ...prev.target_audience.filters,
                    [key]: value
                }
            }
        }));
    };

    const addABVariant = () => {
        setFormData(prev => {
            const newVariants = [...prev.ab_test_config.variants, String.fromCharCode(65 + prev.ab_test_config.variants.length)];
            const equalSplit = 1 / newVariants.length;
            return {
                ...prev,
                ab_test_config: {
                    ...prev.ab_test_config,
                    variants: newVariants,
                    split: newVariants.map(() => equalSplit)
                }
            };
        });
    };

    const removeABVariant = (index) => {
        if (formData.ab_test_config.variants.length <= 2) {
            toast.error('Must have at least 2 variants');
            return;
        }

        setFormData(prev => {
            const newVariants = prev.ab_test_config.variants.filter((_, i) => i !== index);
            const equalSplit = 1 / newVariants.length;
            return {
                ...prev,
                ab_test_config: {
                    ...prev.ab_test_config,
                    variants: newVariants,
                    split: newVariants.map(() => equalSplit)
                }
            };
        });
    };

    // ============================================================================
    // STATUS ACTIONS
    // ============================================================================

    const handleStatusChange = async (campaignId, newStatus) => {
        await updateCampaign(campaignId, { status: newStatus });
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'info',
            active: 'success',
            paused: 'warning',
            completed: 'default',
            cancelled: 'error'
        };
        return colors[status] || 'default';
    };

    const getStatusIcon = (status) => {
        if (status === 'active') return <PlayArrowIcon fontSize="small" />;
        if (status === 'paused') return <PauseIcon fontSize="small" />;
        return null;
    };

    // ============================================================================
    // RENDER HELPERS
    // ============================================================================

    const renderFilters = () => {
        const audienceType = formData.target_audience.type;
        const filters = FILTER_CONFIGS[audienceType] || [];

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Audience Filters
                </Typography>
                <Grid container spacing={2}>
                    {filters.map(filter => (
                        <Grid item xs={12} sm={6} key={filter.key}>
                            {filter.type === 'boolean' ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.target_audience.filters[filter.key] || false}
                                            onChange={(e) => handleFilterChange(filter.key, e.target.checked)}
                                        />
                                    }
                                    label={filter.label}
                                />
                            ) : filter.type === 'number' ? (
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={filter.label}
                                    placeholder={filter.placeholder}
                                    value={formData.target_audience.filters[filter.key] || ''}
                                    onChange={(e) => handleFilterChange(filter.key, parseInt(e.target.value) || '')}
                                />
                            ) : filter.type === 'array' ? (
                                <TextField
                                    fullWidth
                                    label={filter.label}
                                    placeholder={filter.placeholder}
                                    value={formData.target_audience.filters[filter.key]?.join(',') || ''}
                                    onChange={(e) => handleFilterChange(filter.key, e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v)))}
                                    helperText="Comma-separated numbers"
                                />
                            ) : filter.type === 'textarea' ? (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label={filter.label}
                                    placeholder={filter.placeholder}
                                    value={formData.target_audience.filters[filter.key]?.join('\n') || ''}
                                    onChange={(e) => handleFilterChange(filter.key, e.target.value.split('\n').filter(v => v.trim()))}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    label={filter.label}
                                    placeholder={filter.placeholder}
                                    value={formData.target_audience.filters[filter.key] || ''}
                                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    const renderCampaignTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Audience</TableCell>
                        <TableCell>Stats</TableCell>
                        <TableCell>Schedule</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {campaignsData && campaignsData.length > 0 ? (
                        campaignsData.map((campaign) => (
                            <TableRow key={campaign.id}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                        {campaign.name}
                                    </Typography>
                                    {campaign.description && (
                                        <Typography variant="caption" color="textSecondary">
                                            {campaign.description}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip label={campaign.campaign_type} size="small" />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={campaign.status}
                                        color={getStatusColor(campaign.status)}
                                        size="small"
                                        icon={getStatusIcon(campaign.status)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {campaign.target_audience?.type || 'N/A'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="caption" display="block">
                                            Sent: {campaign.stats?.total_sent || 0}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                            Opens: {campaign.stats?.total_opened || 0}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                            Clicks: {campaign.stats?.total_clicked || 0}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" display="block">
                                        {campaign.schedule_config?.frequency || 'N/A'}
                                    </Typography>
                                    {campaign.next_run_at && (
                                        <Typography variant="caption" color="textSecondary">
                                            Next: {new Date(campaign.next_run_at).toLocaleString()}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="View Analytics">
                                            <IconButton
                                                size="small"
                                                onClick={async () => {
                                                    await fetchCampaignDetails(campaign.id);
                                                    await fetchAnalytics(campaign.id);
                                                    await fetchRecipients(campaign.id);
                                                    setAnalyticsDialogOpen(true);
                                                }}
                                            >
                                                <AnalyticsIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Test">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSelectedCampaign(campaign);
                                                    setTestDialogOpen(true);
                                                }}
                                            >
                                                <SendIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={campaign.status === 'active' ? 'Pause' : 'Activate'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleStatusChange(
                                                    campaign.id,
                                                    campaign.status === 'active' ? 'paused' : 'active'
                                                )}
                                            >
                                                {campaign.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => deleteCampaign(campaign.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                No campaigns available.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCreateDialog = () => (
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
                Create New Campaign
                <IconButton
                    onClick={() => setCreateDialogOpen(false)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                    <Tab label="Basic Info" />
                    <Tab label="Audience" />
                    <Tab label="Schedule" />
                    <Tab label="Messages" />
                    <Tab label="A/B Testing" />
                </Tabs>

                {/* Tab 0: Basic Info */}
                {activeTab === 0 && (
                    <Box>
                        <TextField
                            fullWidth
                            label="Campaign Name"
                            value={formData.name}
                            onChange={(e) => handleFormChange('name', e.target.value)}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleFormChange('description', e.target.value)}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Campaign Type</InputLabel>
                            <Select
                                value={formData.campaign_type}
                                onChange={(e) => handleFormChange('campaign_type', e.target.value)}
                                label="Campaign Type"
                            >
                                <MenuItem disabled>
                                    <Typography variant="caption" color="textSecondary">SYSTEM</Typography>
                                </MenuItem>
                                {CAMPAIGN_TYPES.SYSTEM.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                                <MenuItem disabled>
                                    <Typography variant="caption" color="textSecondary">MARKETING</Typography>
                                </MenuItem>
                                {CAMPAIGN_TYPES.MARKETING.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                                <MenuItem disabled>
                                    <Typography variant="caption" color="textSecondary">MAINTENANCE</Typography>
                                </MenuItem>
                                {CAMPAIGN_TYPES.MAINTENANCE.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                                <MenuItem disabled>
                                    <Typography variant="caption" color="textSecondary">EXTERNAL</Typography>
                                </MenuItem>
                                {CAMPAIGN_TYPES.EXTERNAL.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Maps to EmailSettings categories</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status}
                                onChange={(e) => handleFormChange('status', e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="scheduled">Scheduled</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="paused">Paused</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Tab 1: Audience */}
                {activeTab === 1 && (
                    <Box>
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Audience Type</InputLabel>
                            <Select
                                value={formData.target_audience.type}
                                onChange={(e) => handleNestedChange('target_audience.type', e.target.value)}
                                label="Audience Type"
                            >
                                {AUDIENCE_TYPES.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFilters()}
                    </Box>
                )}

                {/* Tab 2: Schedule */}
                {activeTab === 2 && (
                    <Box>
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Frequency</InputLabel>
                            <Select
                                value={formData.schedule_config.frequency}
                                onChange={(e) => handleNestedChange('schedule_config.frequency', e.target.value)}
                                label="Frequency"
                            >
                                <MenuItem value="hourly">Hourly</MenuItem>
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="once">One Time</MenuItem>
                            </Select>
                        </FormControl>

                        {formData.schedule_config.frequency === 'weekly' && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Day of Week</InputLabel>
                                <Select
                                    value={formData.schedule_config.day_of_week}
                                    onChange={(e) => handleNestedChange('schedule_config.day_of_week', e.target.value)}
                                    label="Day of Week"
                                >
                                    <MenuItem value="monday">Monday</MenuItem>
                                    <MenuItem value="tuesday">Tuesday</MenuItem>
                                    <MenuItem value="wednesday">Wednesday</MenuItem>
                                    <MenuItem value="thursday">Thursday</MenuItem>
                                    <MenuItem value="friday">Friday</MenuItem>
                                    <MenuItem value="saturday">Saturday</MenuItem>
                                    <MenuItem value="sunday">Sunday</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        {formData.schedule_config.frequency !== 'hourly' && (
                            <TextField
                                fullWidth
                                type="number"
                                label="Hour (UTC)"
                                value={formData.schedule_config.hour}
                                onChange={(e) => handleNestedChange('schedule_config.hour', parseInt(e.target.value))}
                                margin="normal"
                                inputProps={{ min: 0, max: 23 }}
                            />
                        )}

                        <TextField
                            fullWidth
                            type="number"
                            label="Batch Size"
                            value={formData.schedule_config.batch_size}
                            onChange={(e) => handleNestedChange('schedule_config.batch_size', parseInt(e.target.value))}
                            margin="normal"
                            helperText="Maximum recipients to process per run"
                        />
                    </Box>
                )}

                {/* Tab 3: Messages */}
                {activeTab === 3 && (
                    <Box>
                        {formData.messages.map((message, index) => (
                            <Accordion key={index} defaultExpanded={index === 0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Message {index + 1}</Typography>
                                    {message.subject && (
                                        <Typography variant="caption" sx={{ ml: 2 }} color="textSecondary">
                                            {message.subject}
                                        </Typography>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            value={message.subject}
                                            onChange={(e) => handleMessageChange(index, 'subject', e.target.value)}
                                            margin="normal"
                                            required
                                        />
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Delay (days)"
                                            value={message.delay_days}
                                            onChange={(e) => handleMessageChange(index, 'delay_days', parseInt(e.target.value))}
                                            margin="normal"
                                            helperText="Days after previous message"
                                        />

                                        {formData.ab_test_enabled ? (
                                            formData.ab_test_config.variants.map(variant => (
                                                <TextField
                                                    key={variant}
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    label={`Content - Variant ${variant}`}
                                                    value={message.content_variants[variant] || ''}
                                                    onChange={(e) => handleContentVariantChange(index, variant, e.target.value)}
                                                    margin="normal"
                                                    helperText="Use {{first_name}}, {{email}}, etc. for variables"
                                                />
                                            ))
                                        ) : (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={6}
                                                label="Content"
                                                value={message.content_variants.default || ''}
                                                onChange={(e) => handleContentVariantChange(index, 'default', e.target.value)}
                                                margin="normal"
                                                helperText="Use {{first_name}}, {{email}}, etc. for variables"
                                            />
                                        )}

                                        {formData.messages.length > 1 && (
                                            <Button
                                                color="error"
                                                onClick={() => removeMessage(index)}
                                                sx={{ mt: 2 }}
                                            >
                                                Remove Message
                                            </Button>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addMessage}
                            sx={{ mt: 2 }}
                        >
                            Add Message
                        </Button>
                    </Box>
                )}

                {/* Tab 4: A/B Testing */}
                {activeTab === 4 && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ab_test_enabled}
                                    onChange={(e) => handleFormChange('ab_test_enabled', e.target.checked)}
                                />
                            }
                            label="Enable A/B Testing"
                        />

                        {formData.ab_test_enabled && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Variants
                                </Typography>
                                {formData.ab_test_config.variants.map((variant, index) => (
                                    <Box key={variant} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Chip label={`Variant ${variant}`} sx={{ mr: 2 }} />
                                        <TextField
                                            type="number"
                                            label="Split %"
                                            value={Math.round(formData.ab_test_config.split[index] * 100)}
                                            onChange={(e) => {
                                                const newSplits = [...formData.ab_test_config.split];
                                                newSplits[index] = parseFloat(e.target.value) / 100;
                                                handleNestedChange('ab_test_config.split', newSplits);
                                            }}
                                            sx={{ width: 100, mr: 2 }}
                                        />
                                        {formData.ab_test_config.variants.length > 2 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeABVariant(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={addABVariant}
                                    startIcon={<AddIcon />}
                                >
                                    Add Variant
                                </Button>

                                <FormControl fullWidth margin="normal" sx={{ mt: 3 }}>
                                    <InputLabel>Winning Metric</InputLabel>
                                    <Select
                                        value={formData.ab_test_config.winning_metric}
                                        onChange={(e) => handleNestedChange('ab_test_config.winning_metric', e.target.value)}
                                        label="Winning Metric"
                                    >
                                        <MenuItem value="open_rate">Open Rate</MenuItem>
                                        <MenuItem value="click_rate">Click Rate</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button
                    onClick={createCampaign}
                    variant="contained"
                    disabled={!formData.name || !formData.campaign_type}
                >
                    Create Campaign
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderAnalyticsDialog = () => {
        if (!analytics || !selectedCampaign) return null;

        return (
            <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Analytics: {selectedCampaign.name}
                    <IconButton
                        onClick={() => setAnalyticsDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Overall Stats */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>Overall Performance</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="textSecondary" gutterBottom>Total Sent</Typography>
                                            <Typography variant="h4">{analytics.overall_stats?.total_sent || 0}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="textSecondary" gutterBottom>Opened</Typography>
                                            <Typography variant="h4">{analytics.overall_stats?.total_opened || 0}</Typography>
                                            <Typography variant="caption" color="success.main">
                                                {((analytics.rates?.open_rate || 0) * 100).toFixed(1)}%
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="textSecondary" gutterBottom>Clicked</Typography>
                                            <Typography variant="h4">{analytics.overall_stats?.total_clicked || 0}</Typography>
                                            <Typography variant="caption" color="success.main">
                                                {((analytics.rates?.click_rate || 0) * 100).toFixed(1)}%
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="textSecondary" gutterBottom>Bounced</Typography>
                                            <Typography variant="h4">{analytics.overall_stats?.total_bounced || 0}</Typography>
                                            <Typography variant="caption" color="error.main">
                                                {((analytics.rates?.bounce_rate || 0) * 100).toFixed(1)}%
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* A/B Test Results */}
                        {selectedCampaign.ab_test_enabled && analytics.ab_test_results && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>A/B Test Results</Typography>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Best Variant: <strong>{analytics.best_variant}</strong>
                                </Alert>
                                <Grid container spacing={2}>
                                    {Object.entries(analytics.ab_test_results).map(([variant, stats]) => (
                                        <Grid item xs={4} key={variant}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6">Variant {variant}</Typography>
                                                    <Typography>Sent: {stats.sent}</Typography>
                                                    <Typography>Open Rate: {(stats.open_rate * 100).toFixed(1)}%</Typography>
                                                    <Typography>Click Rate: {(stats.click_rate * 100).toFixed(1)}%</Typography>
                                                    {variant === analytics.best_variant && (
                                                        <Chip label="Winner" color="success" size="small" sx={{ mt: 1 }} />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )}

                        {/* Message Stats */}
                        {analytics.message_stats && analytics.message_stats.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Message Performance</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Sequence</TableCell>
                                                <TableCell>Subject</TableCell>
                                                <TableCell>Sent</TableCell>
                                                <TableCell>Opened</TableCell>
                                                <TableCell>Clicked</TableCell>
                                                <TableCell>Open Rate</TableCell>
                                                <TableCell>Click Rate</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analytics.message_stats.map(msg => (
                                                <TableRow key={msg.sequence_number}>
                                                    <TableCell>{msg.sequence_number}</TableCell>
                                                    <TableCell>{msg.subject}</TableCell>
                                                    <TableCell>{msg.sent}</TableCell>
                                                    <TableCell>{msg.opened}</TableCell>
                                                    <TableCell>{msg.clicked}</TableCell>
                                                    <TableCell>{(msg.open_rate * 100).toFixed(1)}%</TableCell>
                                                    <TableCell>{(msg.click_rate * 100).toFixed(1)}%</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {/* Recipients */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>Recent Recipients</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Variant</TableCell>
                                            <TableCell>Opened</TableCell>
                                            <TableCell>Clicked</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recipients.slice(0, 10).map(recipient => (
                                            <TableRow key={recipient.id}>
                                                <TableCell>{recipient.email}</TableCell>
                                                <TableCell>{recipient.recipient_type}</TableCell>
                                                <TableCell>
                                                    <Chip label={recipient.status} size="small" />
                                                </TableCell>
                                                <TableCell>{recipient.ab_variant || 'N/A'}</TableCell>
                                                <TableCell>{recipient.engagement?.opened ? '✓' : '-'}</TableCell>
                                                <TableCell>{recipient.engagement?.clicked ? '✓' : '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        );
    };

    const renderTestDialog = () => (
        <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Test Email Addresses"
                    value={testEmails}
                    onChange={(e) => setTestEmails(e.target.value)}
                    margin="normal"
                    placeholder="email1@example.com, email2@example.com"
                    helperText="Comma-separated email addresses"
                />
                {selectedCampaign?.ab_test_enabled && (
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Variant</InputLabel>
                        <Select
                            value={testVariant}
                            onChange={(e) => setTestVariant(e.target.value)}
                            label="Variant"
                        >
                            <MenuItem value="default">Default</MenuItem>
                            {selectedCampaign.ab_test_config?.variants.map(variant => (
                                <MenuItem key={variant} value={variant}>{variant}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
                <Button
                    onClick={() => sendTestEmail(selectedCampaign.id)}
                    variant="contained"
                    startIcon={<SendIcon />}
                >
                    Send Test
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderTemplatesDialog = () => (
        <Dialog open={templatesDialogOpen} onClose={() => setTemplatesDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                Campaign Templates
                <IconButton
                    onClick={() => setTemplatesDialogOpen(false)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {templates.map(template => (
                        <Grid item xs={12} key={template.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6">{template.name}</Typography>
                                    <Typography variant="body2" color="textSecondary" paragraph>
                                        {template.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <Chip label={template.campaign_type} size="small" />
                                        <Chip label={template.target_audience?.type} size="small" variant="outlined" />
                                        <Chip label={template.schedule_config?.frequency} size="small" variant="outlined" />
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => createFromTemplate(template.id)}
                                    >
                                        Use Template
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
        </Dialog>
    );

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    if (isLoading && !campaignsData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Error loading campaigns: {error.message}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Email Campaigns</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setTemplatesDialogOpen(true)}
                    >
                        Use Template
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            resetFormData();
                            setActiveTab(0);
                            setCreateDialogOpen(true);
                        }}
                    >
                        Create Campaign
                    </Button>
                </Box>
            </Box>

            {renderCampaignTable()}
            {renderCreateDialog()}
            {renderAnalyticsDialog()}
            {renderTestDialog()}
            {renderTemplatesDialog()}
        </Box>
    );
};

export default InternalCampaigns;